import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useGetPrograms, type Program } from '../api/useGetPrograms';
import { DataTable, type Column } from './DataTable';
import { Pagination } from './Pagination';
import { ProgramStatusBadge } from './ProgramStatusBadge';
import { useFittedPageSize } from '../hooks/useFittedPageSize';

const formatDate = (value: string) =>
  value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const columns: Column<Program>[] = [
  { key: 'uid', header: 'UID', className: 'font-mono text-xs text-gray-400', cell: (p) => `${p.uid.slice(0, 8)}…`, copyValue: (p) => p.uid },
  { key: 'name', header: 'Name', className: 'text-gray-900', cell: (p) => p.name, copyValue: (p) => p.name },
  { key: 'organizer', header: 'Organizer', cell: (p) => p.merchant_name, copyValue: (p) => p.merchant_name },
  { key: 'status', header: 'Status', cell: (p) => <ProgramStatusBadge program={p} /> },
  { key: 'starts', header: 'Start', className: 'text-gray-500', cell: (p) => formatDate(p.starts_at) },
  { key: 'ends', header: 'End', className: 'text-gray-500', cell: (p) => formatDate(p.ends_at) },
];

export const ProgramsTable = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { tableRef, pageSize } = useFittedPageSize();
  const { data, isLoading, isError } = useGetPrograms({ page, limit: pageSize });

  const programs = data?.payload?.programs ?? [];
  const total = data?.payload?.total ?? 0;
  const showPagination = !isLoading && !isError && programs.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rows={programs}
          rowKey={(p) => p.uid}
          onRowClick={(p) => navigate(`/programs/${p.uid}`)}
          isLoading={isLoading}
          isError={isError}
          errorLabel="Failed to load programs."
          emptyLabel="No programs found."
        />
      </div>
      {showPagination && (
        <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
      )}
    </div>
  );
};
